import { Injectable } from '@angular/core';
import { InternalEnvironment } from '../models/models';
import { environment } from '../../environments/environment';

/**
 * The deployment the browser is currently pointed at, resolved from the hostname.
 */
export interface DeployedEnvironment {
	/** True only for the real production portal. */
	isProduction: boolean;
	/** Short label for the warning chip and the tab title, e.g. 'STAGING'. Empty in production. */
	label: string;
}

/**
 * Hostnames that serve the real production internal portal.
 *
 * This is an allowlist on purpose. Every deployed build - prod, staging, devstage, dev and the
 * sandboxes - is built with the same Angular configuration (`developmentPush`), and the environment
 * file it uses is generated at deploy time by the CDK with `production: false` hardcoded for every
 * environment. So `environment.production` cannot be used here; the hostname is the only thing that
 * actually differs. Matching prod explicitly means an unknown or newly added domain is treated as a
 * lower environment and gets the border, rather than silently passing itself off as production.
 *
 * Built in cdk/lib/stacks.ts as `www.${config.rootIntPortalDomain}` for non-sandbox environments.
 */
const productionHostnames: string[] = ['www.prod.internal.trb.bank'];

const localHostnames: string[] = ['localhost', '127.0.0.1', '[::1]'];

const sandboxHostnameSuffix = '.sandbox.internal.trb.bank';

/** Friendly labels for the known lower environments, matched by host suffix, longest suffix first. */
const lowerEnvironmentLabels: { suffix: string; label: string }[] = [
	{ suffix: '.prodstage.internal.trb.bank', label: 'STAGING' },
	{ suffix: '.devstage.internal.trb.bank', label: 'DEVSTAGE' },
	{ suffix: '.relay2.internal.trb.bank', label: 'RELAY2' },
	{ suffix: '.relay.internal.trb.bank', label: 'RELAY' },
	{ suffix: '.dev.internal.trb.bank', label: 'DEV' },
	{ suffix: sandboxHostnameSuffix, label: 'SANDBOX' },
];

/** Label used when the hostname is not production and not one we recognise. */
const unknownEnvironmentLabel = 'NON-PROD';

/** Kept in sync with the `body.lower-environment` rules in styles.scss. */
export const lowerEnvironmentBodyClass = 'lower-environment';
export const environmentLabelAttribute = 'data-environment-label';

@Injectable({
	providedIn: 'root',
})
export class EnvironmentService {
	public environment: InternalEnvironment = environment;

	public readonly deployedEnvironment: DeployedEnvironment;

	constructor() {
		this.deployedEnvironment = this.resolveDeployedEnvironment();
	}

	/**
	 * Marks the page when it is not production, so users who bookmarked a lower environment can tell
	 * at a glance. Adds the red border via a body class and prefixes the tab title with the
	 * environment name - the title is what the browser stores as the name of a new bookmark.
	 *
	 * Does nothing in production, and is safe to call more than once.
	 */
	public applyEnvironmentIndicator(): void {
		if (this.deployedEnvironment.isProduction) {
			return;
		}

		const body = document.body;
		if (body.classList.contains(lowerEnvironmentBodyClass)) {
			return;
		}

		body.classList.add(lowerEnvironmentBodyClass);
		body.setAttribute(environmentLabelAttribute, this.deployedEnvironment.label);
		document.title = `[${this.deployedEnvironment.label}] ${document.title}`;
	}

	private resolveDeployedEnvironment(): DeployedEnvironment {
		// The trailing dot is stripped because a fully-qualified hostname ('www.prod.internal.trb.bank.')
		// is a legal way to reach the same site and must not be mistaken for a lower environment.
		const hostname = window.location.hostname.toLowerCase().replace(/\.$/, '');

		if (productionHostnames.includes(hostname)) {
			return { isProduction: true, label: '' };
		}

		if (localHostnames.includes(hostname)) {
			return { isProduction: false, label: 'LOCAL' };
		}

		const sandboxOwner = this.getSandboxOwner(hostname);
		if (sandboxOwner) {
			return { isProduction: false, label: `SANDBOX (${sandboxOwner})` };
		}

		const knownEnvironment = lowerEnvironmentLabels.find((x) => hostname.endsWith(x.suffix));
		return { isProduction: false, label: knownEnvironment?.label ?? unknownEnvironmentLabel };
	}

	/**
	 * Pulls the developer name out of a sandbox hostname, e.g. `internal.mauriciod.sandbox.internal.trb.bank`
	 * gives 'mauriciod'. Sandboxes all share one suffix, so the owner is what makes the label useful.
	 */
	private getSandboxOwner(hostname: string): string | undefined {
		if (!hostname.endsWith(sandboxHostnameSuffix)) {
			return undefined;
		}

		const labels = hostname.split('.');
		const sandboxIndex = labels.indexOf('sandbox');
		return sandboxIndex > 0 ? labels[sandboxIndex - 1] : undefined;
	}
}
